import { useQuery } from '@tanstack/react-query';
import * as AuthModule from '../contexts/AuthContext';
import { getMe } from '../lib/doctorCalls';
import { doctorAvailable, creditsView, accountHolds, creditsInUse } from '../lib/doctorView';
import { DOCTOR_ME_ROOT, doctorMeKey, doctorOwner } from '../lib/doctorKeys';

/*
 * r16sd: the account half of the Settings Doctor from GET /uiapi/me (DOCTOR-API.md 2.2): credits {posts, targets, enforced} and
 * doctor {accountHolds[], postCreditSlots}. The signed-in /me answer seeds it; while the server's doctor is on (the answer carries
 * `doctor`) one shared query re-reads /me every 60 s, so a top-up clears the banner without a reload. A server without the doctor
 * sends neither key: `available` is false, nothing is polled and nothing is drawn. Read-only: credits are never written from a page.
 * Review MF2: the key carries the signed-in account (lib/doctorKeys.js), so another account in the same tab never reads this one's
 * credits or banners; DOCTOR_ME_KEY is the root, for invalidating every account's entry.
 */
export const DOCTOR_ME_KEY = DOCTOR_ME_ROOT;
const POLL_MS = 60000;

// outside an AuthProvider (a test that mocks the context without it): no user, nothing drawn
const useAuthMaybe = typeof AuthModule.useOptionalAuth === 'function' ? AuthModule.useOptionalAuth : () => null;

export default function useDoctorMe() {
  const auth = useAuthMaybe();
  const user = auth && auth.user ? auth.user : null;
  const on = doctorAvailable(user);
  const q = useQuery({
    queryKey: doctorMeKey(doctorOwner(user)),
    queryFn: () => getMe().then((r) => r.data),
    enabled: on,
    initialData: on ? user : undefined,
    staleTime: POLL_MS,
    refetchInterval: on ? POLL_MS : false,
    retry: 0,
  });
  const me = (on && q.data) || user;
  const available = doctorAvailable(me);
  return {
    available,
    me,
    credits: available ? creditsView(me) : null,
    creditsInUse: available ? creditsInUse(me) : null,
    holds: available ? accountHolds(me) : [],
    postCreditSlots: available && me.doctor && Number.isFinite(Number(me.doctor.postCreditSlots)) ? Number(me.doctor.postCreditSlots) : null,
  };
}
